<script setup>
import { ref, computed } from 'vue';
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue';
import GeneralButton from '@/Components/Links/GeneralButton.vue';
import CardTitle from '@/Components/Links/CardTitle.vue';
import { buildLinkAssetUrl } from '@/utils/assetUrls.js';

const props = defineProps({ link: Object, appearance: Object, buttonStyle: [Object,Array,String], DoBucket:String, DoEndpoint:String });
const emit = defineEmits(['click','share']);
const playing = ref(false);
function openTarget(){
    // Route all clicks through parent so it can enforce lock/unlock flow (same as Typeform)
    emit('click', { link: props.link, mode: mode.value });
}
function onRootClick(e){
    e?.preventDefault?.();
    e?.stopPropagation?.();
    emit('click', { link: props.link, mode: mode.value });
}

const layout = computed(()=> props.link?.options?.layout || 'classic');
const mode   = computed(()=> props.link?.options?.youtube?.display_mode || 'embed');

const isFeatured = computed(()=> layout.value === 'featured');
const isClassic  = computed(()=> layout.value === 'classic');

const isEmbed    = computed(()=> mode.value==='embed' || mode.value==='latest');
const isLink     = computed(()=> mode.value==='link'  || mode.value==='latest_link');
const isLatest   = computed(()=> mode.value==='latest' || mode.value==='latest_link');

function extractVideoId(obj){
    // Accepts the whole link object or a string URL
    const url = typeof obj === 'string' ? obj : (obj?.url || '');
    if (!url) return '';
    try {
        const u = new URL(url);
        // youtu.be/<id>
        if (u.hostname.includes('youtu.be')) {
            return u.pathname.split('/').filter(Boolean)[0] || '';
        }
        // youtube.com/watch?v=<id>
        if (u.searchParams.has('v')) return u.searchParams.get('v') || '';
        // youtube.com/embed/<id> or /shorts/<id>
        const parts = u.pathname.split('/').filter(Boolean);
        const idx = parts.findIndex(p => ['embed','shorts','v'].includes(p));
        if (idx !== -1 && parts[idx+1]) return parts[idx+1];
        // Fallback: last segment
        return parts[parts.length-1] || '';
    } catch(e){
        // Plain ID or malformed URL - try simple regex
        const m = (url || '').match(/[a-zA-Z0-9_-]{11}/);
        return m ? m[0] : '';
    }
}
const fixedVideoId = computed(()=> extractVideoId(props.link));

const latest = computed(()=> props.link?.options?.youtube?.latest || {});
const videoId = computed(()=> isLatest.value ? (latest.value.video_id || fixedVideoId.value || '') : fixedVideoId.value);

// Expose a small API so parent (Index.vue) can trigger inline playback after unlock
defineExpose({
    playInline(){ playing.value = true; },
    // Backwards-compatibility with FacebookCard API name used elsewhere
    playUrl(){ playing.value = true; }
});

const targetUrl = computed(()=>{
    // For 'link' and 'latest_link' modes, always use the original link.url provided by user
    if (isLink.value && props.link?.url) return props.link.url;
    if (fixedVideoId.value) return `https://www.youtube.com/watch?v=${fixedVideoId.value}`;
    // Fallbacks
    if (latest.value?.video_id) return `https://www.youtube.com/watch?v=${latest.value.video_id}`;
    return props.link?.url || '';
});

const embedUrl = computed(()=>{
    if (!videoId.value) return '';
    const autoplay = playing.value ? 1 : (props.link.options?.youtube?.autoplay ? 1 : 0);
    const mute     = props.link.options?.youtube?.mute ? 1 : 0;
    return `https://www.youtube-nocookie.com/embed/${videoId.value}?autoplay=${autoplay}&mute=${mute}&rel=0&modestbranding=1&playsinline=1`;
});

const thumbUrl = computed(()=>{
    const file = props.link?.file;
    if (file) return buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, file);
    return videoId.value ? `https://i.ytimg.com/vi/${videoId.value}/hqdefault.jpg` : '';
});

function togglePlay(){
    // In classic+embed we want slide down on click; toggle to allow hide
    playing.value = !playing.value;
}
function start(e){
    e?.stopPropagation?.();
    // Let parent enforce lock/unlock flow before starting inline playback (match Facebook behavior)
    emit('click', { link: props.link, mode: mode.value, action: 'play' });
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link); }
</script>

<template>
    <!-- FEATURED variant (big card) -->
    <GeneralEmbedButton v-if="isFeatured"
                        class="yt"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        :layout="layout"
                        @click="onRootClick">

                        <div class="box"  :class="[appearance.buttons]">
                        <!-- Link modes: open YouTube -->
                        <a v-if="isLink" class="pane link" :href="targetUrl" rel="noopener nofollow" target="_blank" @click.prevent.stop="onRootClick">
                            <img class="thumb" :src="thumbUrl" :alt="link.title || 'YouTube thumb'"/>
                            <div class="video-badge no-bg">
                                <div class="title-row">
                                <span class="brand-chip">
                                  <svg height="100%" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;" version="1.1" viewBox="0 0 512 512" width="100%" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:serif="http://www.serif.com/" xmlns:xlink="http://www.w3.org/1999/xlink"><g><path d="M501.299,132.766c-5.888,-22.03 -23.234,-39.377 -45.264,-45.264c-39.932,-10.701 -200.037,-10.701 -200.037,-10.701c0,0 -160.105,0 -200.038,10.701c-22.025,5.887 -39.376,23.234 -45.264,45.264c-10.696,39.928 -10.696,123.236 -10.696,123.236c0,0 0,83.308 10.696,123.232c5.888,22.03 23.239,39.381 45.264,45.268c39.933,10.697 200.038,10.697 200.038,10.697c0,0 160.105,0 200.037,-10.697c22.03,-5.887 39.376,-23.238 45.264,-45.268c10.701,-39.924 10.701,-123.232 10.701,-123.232c0,0 0,-83.308 -10.701,-123.236Z" style="fill:#ed1f24;fill-rule:nonzero;"/><path d="M204.796,332.803l133.018,-76.801l-133.018,-76.801l0,153.602Z" style="fill:#fff;fill-rule:nonzero;"/></g></svg>
                                </span>
                                    <CardTitle :title="link.title || 'YouTube'" layout="featured" align="left" />
                                </div>
                            </div>
                            <button class="share" @click.prevent.stop="onShare" aria-label="Share">
                                <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g>
                                        <path fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
                                              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
                                    </g>
                                </svg>
                            </button>
                        </a>

                        <!-- Embed modes: thumb -> iframe -->
                        <template v-else>
                <template v-if="!playing">
                    <img class="thumb" :src="thumbUrl" :alt="link.title || 'YouTube thumb'"/>
                    <button class="play" @click.stop="start" aria-label="Play">
                        <svg viewBox="0 0 60 60" width="40" height="40"><circle cx="28" cy="30" r="28" fill="#fff" opacity=".9"/><path d="M22 20 L38 30 L22 40 Z" fill="#111"/></svg>
                    </button>
                    <div class="video-badge no-bg">
                        <div class="title-row">
                        <span class="brand-chip">
                           <svg height="100%" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:1.41421;" version="1.1" viewBox="0 0 24 24" width="100%" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:serif="http://www.serif.com/" xmlns:xlink="http://www.w3.org/1999/xlink"><rect height="24" id="Artboard15" style="fill:none;" width="24" x="0" y="0"/><g><path d="M2.093,9.075c0.125,-1.941 1.629,-3.509 3.562,-3.716c2.005,-0.202 4.136,-0.311 6.345,-0.311c2.209,0 4.34,0.109 6.345,0.312c1.933,0.206 3.437,1.774 3.562,3.715c0.061,0.956 0.093,1.933 0.093,2.925c0,0.992 -0.032,1.969 -0.093,2.925c-0.125,1.941 -1.629,3.509 -3.562,3.716c-2.005,0.202 -4.136,0.311 -6.345,0.311c-2.209,0 -4.34,-0.109 -6.345,-0.312c-1.933,-0.206 -3.437,-1.774 -3.562,-3.715c-0.061,-0.956 -0.093,-1.933 -0.093,-2.925c0,-0.992 0.032,-1.969 0.093,-2.925Z" style="fill:#f00;"/><path d="M15.055,12l-4.909,2.995l0,-5.99l4.909,2.995Z" style="fill:#fff;"/></g></svg>

                        </span>
                            <CardTitle :title="link.title || 'YouTube'" layout="featured" align="left" />
                        </div>
                    </div>
                    <button class="share" v-if="!playing" @click.stop="onShare" aria-label="Share">
                        <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g>
                                <path fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
                                      d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
                            </g>
                        </svg>
                    </button>
                </template>
                <template v-else>
                    <button class="close-btn" @click.stop="playing=false" aria-label="Close video">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.42L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/>
                        </svg>
                    </button>
                    <iframe :src="embedUrl" allow="autoplay; encrypted-media; picture-in-picture; web-share"
                            referrerpolicy="strict-origin-when-cross-origin" loading="eager" frameborder="0" allowfullscreen/>
                </template>
            </template>
        </div>
    </GeneralEmbedButton>

    <GeneralButton v-else-if="isClassic"
                   class="yt classic"
                   :link="link"
                   :appearance="appearance"
                   :buttonStyle="buttonStyle"
                   :DoBucket="DoBucket"
                   :DoEndpoint="DoEndpoint"
                   :layout="layout"
                   :hideLeftImage="isEmbed && playing"
                   @click="openTarget"
    >
        <template #left>
            <!-- Use platform icon to keep uniform size like other classic cards -->
            <img src="/template/allmyonline/assets/icons/youtube.svg" alt="YouTube" class="classic-icon" />
        </template>
        <template #title>
            <CardTitle :title="link.title || 'YouTube'" layout="classic" align="center" :max="100" />
        </template>
        <template #actions>
            <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                    <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                          d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
                </g>
            </svg>
        </template>

        <transition name="slide-fade">
            <div v-if="isEmbed && playing" class="classic-embed mt-2 w-100">
                <div class="box" :class="[appearance.buttons]">
                    <button class="close-btn" @click.stop="playing=false" aria-label="Close video">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.42L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/>
                        </svg>
                    </button>
                    <iframe :src="embedUrl" allow="autoplay; encrypted-media; picture-in-picture; web-share" loading="eager" frameborder="0" allowfullscreen/>
                </div>
            </div>
        </transition>
    </GeneralButton>

</template>

<style scoped>
.yt.featured .box{position: relative;aspect-ratio:16/9; background:#000;  overflow:hidden;}
.yt.featured .pane.link, .yt.featured .box iframe, .yt.featured .box .thumb{position:absolute; inset:0; width:100%; height:100%;}
.yt.featured .thumb{object-fit:cover;}
.yt.featured .play{position:absolute; inset:0; display:grid; place-items:center; border:0; background:transparent; cursor:pointer;}
.yt.featured .badge {
    position: absolute !important;  /* force absolute positioning */
    bottom: 10px !important;        /* pin to bottom */
    left: 10px !important;          /* pin to left */
    display: inline-block;          /* shrink to content */
    width: auto !important;         /* not 100% */
    max-width: calc(100% - 60px);   /* leave room for share */
    padding: 6px 10px;
    background: rgba(0,0,0,0.6);
    color: #fff;
    font-size: 14px;
    line-height: 1.3;
    border-radius: .5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 2;
}
.video-badge {
    position: absolute !important;  /* force absolute positioning */
    bottom: 10px !important;        /* pin to bottom */
    left: 10px !important;          /* pin to left */
    display: inline-block;          /* shrink to content */
    width: auto !important;         /* not 100% */
    max-width: calc(100% - 60px);   /* leave room for share */
    padding: 6px 10px !important;
    background: rgba(0,0,0,0.6);
    color: #fff;
    font-size: 14px;
    line-height: 1.3;
    border-radius: .5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 2;
}

.yt.featured .share, .yt.featured .open{position:absolute; right:16px; bottom:10px; width:24px; height:24px; display:grid; place-items:center; border-radius:50%; color:#fff; z-index:3; border:0;}
.classic-embed .box{position:relative; aspect-ratio:16/9; background:#000; overflow:hidden;}
.classic-embed iframe{position:absolute; inset:0; width:100%; height:100%;}
/* Slide down transition */
.slide-fade-enter-from,.slide-fade-leave-to{max-height:0; opacity:0; transform:translateY(-6px);}
.slide-fade-enter-active,.slide-fade-leave-active{transition:all .25s ease;}
.slide-fade-enter-to,.slide-fade-leave-from{max-height:1000px; opacity:1; transform:translateY(0);}
/* Close button overlay over video */
.close-btn{position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4;}
.close-btn:hover{background:rgba(0,0,0,.6);}
.yt.featured .video-badge.no-bg{ background: transparent; padding: 0; }
.title-row{ display: inline-flex; align-items: center; gap: 8px; }
.brand-chip{ width: 1.5rem; height: auto; opacity: 0.85; display: inline-grid; place-items: center; }
.title-icon{ width: 16px; height: 16px; display:block; object-fit: contain; }
</style>
